import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import * as LucideIcons from "lucide-react";
import { Plus, Save, Trash2, ChevronDown, ChevronUp, FolderTree, X } from "lucide-react";

const CATEGORY_ICONS = [
  "Scissors", "User", "Users", "UserCheck", "Baby", "Heart", "Sparkles",
  "Crown", "Brush", "Wand2", "Flower2", "Flower", "Palette", "Smile",
];

const DEFAULT_ICON = "Scissors";

function CategoryIcon({ name, className = "w-4 h-4", strokeWidth = 1.5 }) {
  const Icon = LucideIcons[name] || LucideIcons[DEFAULT_ICON];
  return <Icon className={className} strokeWidth={strokeWidth} />;
}

export default function ServicesManager() {
  const { t } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [addons, setAddons] = useState([]);
  const [newAddon, setNewAddon] = useState({ name: "", description: "", duration_minutes: 15, price: 0, active: true });

  const load = async () => {
    const [s, st, c] = await Promise.all([
      api.get("/cms/services"),
      api.get("/cms/staff"),
      api.get("/cms/service-categories"),
    ]);
    setItems(s.data || []);
    setStaff(st.data || []);
    setCategories(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const loadAddons = async (serviceId) => {
    const { data } = await api.get(`/cms/services/${serviceId}/addons`);
    setAddons(data);
  };

  const openEdit = (s) => {
    setEditing(s);
    if (s?.id) loadAddons(s.id); else setAddons([]);
  };

  const save = async () => {
    const payload = {
      name: editing.name, description: editing.description || "",
      duration_minutes: parseInt(editing.duration_minutes) || 30,
      price: parseFloat(editing.price) || 0,
      category: editing.category || null,
      category_id: editing.category_id || null,
      active: !!editing.active,
      buffer_before_minutes: parseInt(editing.buffer_before_minutes) || 0,
      buffer_after_minutes: parseInt(editing.buffer_after_minutes) || 0,
      staff_ids: editing.staff_ids || [],
    };
    if (editing.id) {
      await api.put(`/cms/services/${editing.id}`, payload);
      setEditing(null); load();
    } else {
      const { data } = await api.post("/cms/services", payload);
      setEditing(data); loadAddons(data.id); load();
    }
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/cms/services/${id}`); load(); };

  const toggleStaff = (id) => {
    const cur = editing.staff_ids || [];
    setEditing({ ...editing, staff_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const addAddon = async () => {
    if (!editing.id || !newAddon.name) return;
    await api.post("/cms/addons", { ...newAddon, service_id: editing.id, duration_minutes: parseInt(newAddon.duration_minutes) || 0, price: parseFloat(newAddon.price) || 0 });
    setNewAddon({ name: "", description: "", duration_minutes: 15, price: 0, active: true });
    loadAddons(editing.id);
  };
  const removeAddon = async (aid) => { await api.delete(`/cms/addons/${aid}`); loadAddons(editing.id); };
  const updateAddonField = async (aid, patch) => {
    const a = addons.find((x) => x.id === aid);
    await api.put(`/cms/addons/${aid}`, { ...a, ...patch, service_id: editing.id });
    loadAddons(editing.id);
  };

  // --- Categories ---
  const saveCategory = async () => {
    const payload = {
      name: editingCategory.name,
      icon: editingCategory.icon || DEFAULT_ICON,
      order: parseInt(editingCategory.order) || 0,
      active: editingCategory.active !== false,
    };
    if (editingCategory.id) {
      await api.put(`/cms/service-categories/${editingCategory.id}`, payload);
    } else {
      await api.post("/cms/service-categories", payload);
    }
    setEditingCategory(null); load();
  };
  const removeCategory = async (id) => {
    if (!window.confirm("Kategorie löschen? Zugeordnete Leistungen behalten ihre Daten, verlieren aber die Kategorie.")) return;
    await api.delete(`/cms/service-categories/${id}`);
    load();
  };
  const moveCategory = async (cat, direction) => {
    const idx = categories.findIndex((c) => c.id === cat.id);
    const swap = direction === "up" ? categories[idx - 1] : categories[idx + 1];
    if (!swap) return;
    await Promise.all([
      api.put(`/cms/service-categories/${cat.id}`, { ...cat, order: swap.order }),
      api.put(`/cms/service-categories/${swap.id}`, { ...swap, order: cat.order }),
    ]);
    load();
  };

  if (editing) {
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-3xl">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl">{editing.id ? `${t("service")} bearbeiten` : `Neue ${t("service")}`}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="mv-btn-ghost">Schließen</button>
            <button data-testid="save-service-btn" onClick={save} className="mv-btn"><Save className="w-4 h-4" /> Speichern</button>
          </div>
        </div>
        <div className="mv-card p-6 space-y-4">
          <div><label className="block text-xs mb-1">Name</label><input className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
          <div><label className="block text-xs mb-1">Beschreibung</label><textarea className="mv-input min-h-[80px]" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs mb-1">Dauer (Min)</label><input type="number" className="mv-input" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })} /></div>
            <div><label className="block text-xs mb-1">Preis (€)</label><input type="number" step="0.01" className="mv-input" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
            <div>
              <label className="block text-xs mb-1">Kategorie</label>
              <select className="mv-input" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value || null })} data-testid="service-edit-category">
                <option value="">— Keine —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs mb-1">Puffer davor (Min)</label><input type="number" className="mv-input" value={editing.buffer_before_minutes || 0} onChange={(e) => setEditing({ ...editing, buffer_before_minutes: e.target.value })} /></div>
            <div><label className="block text-xs mb-1">Puffer danach (Min)</label><input type="number" className="mv-input" value={editing.buffer_after_minutes || 0} onChange={(e) => setEditing({ ...editing, buffer_after_minutes: e.target.value })} /></div>
          </div>
          <div>
            <label className="block text-xs mb-1">Mitarbeiter (leer = alle)</label>
            <div className="flex flex-wrap gap-2">
              {staff.length === 0 ? <p className="text-xs italic" style={{ color: "var(--mv-text-muted)" }}>Keine Mitarbeiter.</p> : staff.map((s) => {
                const sel = (editing.staff_ids || []).includes(s.id);
                return (
                  <button type="button" key={s.id} onClick={() => toggleStaff(s.id)} className={`px-3 py-1.5 rounded-full text-xs border ${sel ? "text-white" : ""}`} style={sel ? { background: "var(--mv-primary)", borderColor: "var(--mv-primary)" } : { borderColor: "var(--mv-border)" }}>{s.name}</button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Aktiv (für Buchung sichtbar)</label>
        </div>

        {editing.id && (
          <div className="mv-card p-6 mt-6">
            <p className="mv-label mb-3">Zubuchbare Zusatzleistungen</p>
            <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>Verlängern Slotdauer + Preis dieser Hauptleistung (z.B. „Augenbrauen zupfen", „Bart trimmen").</p>
            <div className="space-y-2 mb-4">
              {addons.map((a) => (
                <div key={a.id} className="grid grid-cols-[1fr_90px_90px_50px_auto] gap-2 items-center p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)" }} data-testid={`addon-${a.id}`}>
                  <input className="mv-input text-sm" value={a.name} onChange={(e) => updateAddonField(a.id, { name: e.target.value })} />
                  <input type="number" className="mv-input text-sm" value={a.duration_minutes} onChange={(e) => updateAddonField(a.id, { duration_minutes: parseInt(e.target.value) || 0 })} />
                  <input type="number" step="0.01" className="mv-input text-sm" value={a.price} onChange={(e) => updateAddonField(a.id, { price: parseFloat(e.target.value) || 0 })} />
                  <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!a.active} onChange={(e) => updateAddonField(a.id, { active: e.target.checked })} />Aktiv</label>
                  <button onClick={() => removeAddon(a.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-[1fr_90px_90px_auto] gap-2">
              <input data-testid="new-addon-name" placeholder="Name" className="mv-input text-sm" value={newAddon.name} onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })} />
              <input type="number" placeholder="Min" className="mv-input text-sm" value={newAddon.duration_minutes} onChange={(e) => setNewAddon({ ...newAddon, duration_minutes: e.target.value })} />
              <input type="number" step="0.01" placeholder="€" className="mv-input text-sm" value={newAddon.price} onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })} />
              <button data-testid="add-addon-btn" onClick={addAddon} className="mv-btn-ghost text-xs"><Plus className="w-3 h-3" /> Hinzufügen</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div><p className="mv-label mb-2">{t("booking")}</p><h1 className="font-display text-4xl">{t("service_plural")}</h1></div>
        <div className="flex gap-2">
          <button data-testid="toggle-categories-btn" onClick={() => setShowCategories(!showCategories)} className="mv-btn-ghost">
            <FolderTree className="w-4 h-4" /> Kategorien {showCategories ? "ausblenden" : "verwalten"}
          </button>
          <button data-testid="new-service-btn" onClick={() => openEdit({ name: "", description: "", duration_minutes: 30, price: 0, category: "", category_id: categories[0]?.id || "", active: true, buffer_before_minutes: 0, buffer_after_minutes: 10, staff_ids: [] })} className="mv-btn"><Plus className="w-4 h-4" /> Neue Leistung</button>
        </div>
      </div>

      {showCategories && (
        <div className="mv-card p-6 mb-8" data-testid="categories-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl">Kategorien</h2>
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Z. B. Damen, Herren, Kinder — wird im Buchungsflow als erster Schritt angezeigt.</p>
            </div>
            <button data-testid="new-category-btn" onClick={() => setEditingCategory({ name: "", icon: DEFAULT_ICON, order: (categories[categories.length - 1]?.order || 0) + 10, active: true })} className="mv-btn-ghost text-sm">
              <Plus className="w-4 h-4" /> Neue Kategorie
            </button>
          </div>
          {categories.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: "var(--mv-text-muted)" }}>Noch keine Kategorien angelegt.</p>
          ) : (
            <div className="space-y-2">
              {categories.map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)" }} data-testid={`category-row-${c.id}`}>
                  <CategoryIcon name={c.icon} className="w-5 h-5" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>
                      {items.filter((s) => s.category_id === c.id).length} Leistungen · {c.active ? "Aktiv" : "Inaktiv"}
                    </p>
                  </div>
                  <button disabled={i === 0} onClick={() => moveCategory(c, "up")} className="p-1.5 opacity-60 hover:opacity-100 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button disabled={i === categories.length - 1} onClick={() => moveCategory(c, "down")} className="p-1.5 opacity-60 hover:opacity-100 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => setEditingCategory(c)} className="mv-btn-ghost text-xs" data-testid={`edit-category-${c.id}`}>Bearbeiten</button>
                  <button onClick={() => removeCategory(c.id)} className="text-xs text-red-500 px-2">Löschen</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => {
          const cat = categories.find((c) => c.id === s.category_id);
          return (
            <div key={s.id} onClick={() => openEdit(s)} className="mv-card p-5 cursor-pointer" data-testid={`service-card-${s.id}`}>
              {cat ? (
                <p className="text-xs mb-1 flex items-center gap-1.5" style={{ color: "var(--mv-secondary)" }}>
                  <CategoryIcon name={cat.icon} className="w-3 h-3" /> {cat.name}
                </p>
              ) : s.category ? (
                <p className="text-xs mb-1" style={{ color: "var(--mv-text-muted)" }}>{s.category}</p>
              ) : null}
              <h3 className="font-display text-lg">{s.name}</h3>
              <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>{s.duration_minutes} Min · {s.price?.toFixed(2)} € · Puffer {s.buffer_before_minutes || 0}/{s.buffer_after_minutes || 0}</p>
              <p className="text-xs mt-2" style={{ color: s.active ? "var(--mv-secondary)" : "var(--mv-text-muted)" }}>{s.active ? "● Aktiv" : "○ Inaktiv"}</p>
              <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-xs text-red-500 mt-2">Löschen</button>
            </div>
          );
        })}
      </div>

      {editingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={() => setEditingCategory(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{editingCategory.id ? "Kategorie bearbeiten" : "Neue Kategorie"}</h2>
              <button onClick={() => setEditingCategory(null)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1">Name</label>
                <input className="mv-input" value={editingCategory.name} onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })} data-testid="category-edit-name" placeholder="z. B. Damen, Herren, Kinder" />
              </div>
              <div>
                <label className="block text-xs mb-2">Icon</label>
                <div className="grid grid-cols-7 gap-2">
                  {CATEGORY_ICONS.map((iname) => {
                    const sel = editingCategory.icon === iname;
                    return (
                      <button
                        key={iname}
                        type="button"
                        onClick={() => setEditingCategory({ ...editingCategory, icon: iname })}
                        data-testid={`icon-${iname}`}
                        className="aspect-square flex items-center justify-center rounded-lg border transition-colors"
                        style={{
                          background: sel ? "var(--mv-primary)" : "transparent",
                          color: sel ? "#fff" : "inherit",
                          borderColor: sel ? "var(--mv-primary)" : "var(--mv-border)",
                        }}
                      >
                        <CategoryIcon name={iname} className="w-5 h-5" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Reihenfolge</label>
                  <input type="number" className="mv-input" value={editingCategory.order} onChange={(e) => setEditingCategory({ ...editingCategory, order: e.target.value })} />
                </div>
                <label className="flex items-end gap-2 text-sm pb-2">
                  <input type="checkbox" checked={editingCategory.active !== false} onChange={(e) => setEditingCategory({ ...editingCategory, active: e.target.checked })} />
                  Aktiv (sichtbar)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditingCategory(null)} className="mv-btn-ghost">Abbrechen</button>
              <button onClick={saveCategory} className="mv-btn" data-testid="save-category-btn"><Save className="w-4 h-4" /> Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
